import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  applyPax8CustomerMappings,
  applyPax8SubscriptionMappings,
  bulkResolvePax8Mappings,
  bulkUpdatePax8SubscriptionMappings,
  connectPax8,
  getPax8CustomerMappings,
  getPax8ImportLog,
  getPax8Settings,
  getPax8SubscriptionImportLog,
  getPax8SubscriptionMappings,
  resolvePax8Mapping,
  startPax8Sync,
  syncPax8Customers,
  syncPax8Subscriptions,
  updatePax8Settings,
  updatePax8SubscriptionMapping,
} from "controller/api/integration/pax8";
import type { ApiErrorType } from "shared";


import type {
  ConnectPax8Input,
  Pax8AutoMapSettings,
  Pax8SubscriptionStatus,
} from "shared/types/integrations";
import { sentryLogger } from "utilz";

export const useConnectPax8 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ConnectPax8Input) => connectPax8(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrationSources"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "connectPax8",
      });
    },
  });
};

export const useGetPax8Settings = ({ enabled = true } = {}) => {
  return useQuery({
    queryKey: ["pax8Settings"],
    queryFn: () => getPax8Settings(),
    enabled,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "getPax8Settings",
      });
    },
  });
};

export const useStartPax8Sync = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => startPax8Sync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pax8Settings"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "startPax8Sync",
      });
    },
  });
};

export const useUpdatePax8Settings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: Pax8AutoMapSettings) => updatePax8Settings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pax8Settings"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "updatePax8Settings",
      });
    },
  });
};

export const useSyncPax8Customers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => syncPax8Customers(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pax8CustomerMappings"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "syncPax8Customers",
      });
    },
  });
};

export const useGetPax8CustomerMappings = (params: {
  status?: string;
  q?: string;
  page?: number;
  limit?: number;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
}) => {
  return useQuery({
    queryKey: ["pax8CustomerMappings", params],
    queryFn: () => getPax8CustomerMappings(params),
    keepPreviousData: true,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "getPax8CustomerMappings",
      });
    },
  });
};

export const useResolvePax8Mapping = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mappingId,
      body,
    }: {
      mappingId: string;
      body: Parameters<typeof resolvePax8Mapping>[1];
    }) => resolvePax8Mapping(mappingId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pax8CustomerMappings"] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "resolvePax8Mapping",
      });
    },
  });
};

export const useBulkResolvePax8Mappings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof bulkResolvePax8Mappings>[0]) =>
      bulkResolvePax8Mappings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pax8CustomerMappings"] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "bulkResolvePax8Mappings",
      });
    },
  });
};

export const useApplyPax8CustomerMappings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => applyPax8CustomerMappings(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["pax8CustomerMappings"] });
      queryClient.setQueryData(["pax8ImportLog"], data);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "applyPax8CustomerMappings",
      });
    },
  });
};

export const useGetPax8ImportLog = () => {
  return useQuery({
    queryKey: ["pax8ImportLog"],
    queryFn: () => getPax8ImportLog(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "getPax8ImportLog",
      });
    },
  });
};

export const useSyncPax8Subscriptions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => syncPax8Subscriptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pax8SubscriptionMappings"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "syncPax8Subscriptions",
      });
    },
  });
};

export const useGetPax8SubscriptionMappings = (params: {
  status: "all" | Pax8SubscriptionStatus;
  q?: string;
  customerId?: string;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
}) => {
  return useQuery({
    queryKey: ["pax8SubscriptionMappings", params],
    queryFn: () => getPax8SubscriptionMappings(params),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "getPax8SubscriptionMappings",
      });
    },
  });
};

export const useUpdatePax8SubscriptionMapping = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mappingId,
      body,
    }: {
      mappingId: string;
      body: {
        invoice_alias?: string;
        recurring_invoice_id?: string;
        recurring_invoice_name?: string;
      };
    }) => updatePax8SubscriptionMapping(mappingId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pax8SubscriptionMappings"] });
      queryClient.invalidateQueries({ queryKey: ["recurringInvoice"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "updatePax8SubscriptionMapping",
      });
    },
  });
};

export const useBulkUpdatePax8SubscriptionMappings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Parameters<typeof bulkUpdatePax8SubscriptionMappings>[0],
    ) => bulkUpdatePax8SubscriptionMappings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pax8SubscriptionMappings"] });
      queryClient.invalidateQueries({ queryKey: ["recurringInvoice"] });
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "bulkUpdatePax8SubscriptionMappings",
      });
    },
  });
};

export const useApplyPax8SubscriptionMappings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => applyPax8SubscriptionMappings(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["pax8SubscriptionMappings"] });
      if (data?.import_result) {
        queryClient.setQueryData(
          ["pax8SubscriptionImportLog"],
          data.import_result,
        );
      } else {
        queryClient.invalidateQueries({
          queryKey: ["pax8SubscriptionImportLog"],
        });
      }
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "applyPax8SubscriptionMappings",
      });
    },
  });
};

export const useGetPax8SubscriptionImportLog = () => {
  return useQuery({
    queryKey: ["pax8SubscriptionImportLog"],
    queryFn: () => getPax8SubscriptionImportLog(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error,
        workflow: "integration",
        functionName: "getPax8SubscriptionImportLog",
      });
    },
  });
};

